import { makeAutoObservable } from "mobx";
import ApiManager from "../helpers/ApiManager";
import CallStore from "./CallStore";

/**
 * Recording store for managing call recording functionality
 * Handles recording start/stop, status queries, and file processing
 */
class recordStore {

  /**
   * Initialize the recording store
   * Sets up MobX observables for reactive state management
   */
  constructor() {
    makeAutoObservable(this);
  }

  /** @type {Object|null} Current recording data */
  record = null;

  /** @type {boolean} Whether recording is currently active */
  isRecording = false;

  /** @type {string|null} Current recording ID */
  recordingId = null;

  /**
   * Set the current recording data
   * @param {Object} record - Recording object with metadata
   * @returns {void}
   */
  setRecord(record) {
    this.record = record;
  }

  /**
   * Set recording state
   * @param {boolean} isRecording - Whether recording is active
   * @returns {void}
   */
  setIsRecording(isRecording) {
    this.isRecording = isRecording;
  }

  /**
   * Start recording for a call session
   * @async
   * @param {string} slug - Call session identifier
   * @returns {Promise<Object>} API response with recording data
   */
  async startRecording(slug) {
    const response = await ApiManager.request(`calls/${slug}/recording/start`, 'POST');
    return response;
  }

  /**
   * Stop recording for a call session
   * Updates call notes with recording URL
   * @async
   * @param {string} slug - Call session identifier
   * @param {string} recordingId - Recording session ID
   * @returns {Promise<Object>} API response with recording data
   */
  async stopRecording(slug, recordingId) {

    try {
      const response = await ApiManager.request(`calls/${slug}/recording/${recordingId}/stop`, 'POST');
      if (response.success) {
        this.isRecording = false;
        const prevNote = CallStore.call?.note || '';
        CallStore.setCall({ ...CallStore.call, note: prevNote + " \n\n  Recording URL: " + response.data.presignedUrl });
      }
      return response;
    } catch (error) {
      return {
        success: false,
        message: error.response.data.message,
      };
    }

  }

  /**
   * Get recording status for a call session
   * @async
   * @param {string} slug - Call session identifier
   * @returns {Promise<Object>} API response with recording status
   */
  async getCallRecordingStatus(slug) {
    if (!slug) {
      return {
        success: false,
        message: 'Slug is required'
      };
    }
    const response = await ApiManager.request(`calls/${slug}/recording/status`, 'GET');
    return response;
  }

}

const RecordStore = new recordStore();

export default RecordStore; 